import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Clock3,
  Factory,
  Gauge,
  Pencil,
  Plus,
  Settings2,
  Trash2,
  Wrench,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useDebounce } from "@/hooks/useDebounce";
import { useMachinery } from "@/hooks/useMachinery";
import {
  MACHINERY_DAYS,
  MACHINERY_STATUSES,
  Machinery,
  MachineryShift,
  MachineryStatus,
} from "@/types/machinery";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { CardGridSkeleton } from "@/components/shared/CardGridSkeleton";

const emptyMachineForm = {
  name: "",
  type: "",
  description: "",
  acquisition_date: "",
  status: "Disponible" as MachineryStatus,
  daily_capacity_hours: "8",
  technical_capacity_ratio: "0",
  technical_capacity_unit: "",
  technical_capacity_interval_minutes: "60",
};

const emptyShiftForm = {
  day_of_week: "1",
  start_time: "08:00",
  end_time: "17:00",
  is_active: true,
};

const statusColorMap: Record<MachineryStatus, string> = {
  Disponible: "bg-emerald-100 text-emerald-700 border-emerald-200",
  "En Uso": "bg-blue-100 text-blue-700 border-blue-200",
  Mantenimiento: "bg-amber-100 text-amber-700 border-amber-200",
  Fallo: "bg-red-100 text-red-700 border-red-200",
};

const formatDate = (value?: string | null) => {
  if (!value) return "N/D";
  return new Date(`${value}T00:00:00`).toLocaleDateString("es-SV", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const toMinutes = (time: string) => {
  const parts = time.split(":").map(Number);
  if (parts.length < 2) return null;
  const [hours, minutes] = parts;
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return (hours * 60) + minutes;
};

const calculateShiftFormWarnings = (
  shiftForm: typeof emptyShiftForm,
  existingShifts: MachineryShift[],
  selectedMachine: Machinery | null,
  editingShiftId: string | null,
): string[] => {
  const warnings: string[] = [];

  const day = Number(shiftForm.day_of_week);
  const start = toMinutes(shiftForm.start_time);
  const end = toMinutes(shiftForm.end_time);

  if (!Number.isInteger(day) || day < 0 || day > 6) {
    warnings.push("Selecciona un día válido de la semana.");
    return warnings;
  }

  if (start === null || end === null) {
    warnings.push("Las horas deben estar en formato HH:mm.");
    return warnings;
  }

  if (end <= start) {
    warnings.push("La hora de fin debe ser mayor que la hora de inicio.");
  }

  if (!selectedMachine) return warnings;

  if (shiftForm.is_active && selectedMachine.status === "Fallo") {
    warnings.push("La máquina está en estado Fallo y no admite turnos activos.");
  }

  const activeShifts = existingShifts.filter((shift) => shift.is_active && shift.id !== editingShiftId);

  if (shiftForm.is_active) {
    const overlap = activeShifts.find((shift) => {
      if (shift.day_of_week !== day) return false;
      const shiftStart = toMinutes(shift.start_time);
      const shiftEnd = toMinutes(shift.end_time);
      if (shiftStart === null || shiftEnd === null) return false;
      return start < shiftEnd && shiftStart < end;
    });

    if (overlap) {
      warnings.push(
        `Existe solapamiento con turno ${overlap.day_label} ${overlap.display_start_time}-${overlap.display_end_time}.`,
      );
    }

    const existingDayHours = activeShifts
      .filter((shift) => shift.day_of_week === day)
      .reduce((acc, shift) => acc + shift.duration_hours, 0);

    const candidateHours = (end - start) / 60;
    const projectedHours = existingDayHours + candidateHours;

    if (projectedHours - selectedMachine.daily_capacity_hours > 0.0001) {
      warnings.push(
        `Las horas del día (${projectedHours.toFixed(2)}h) exceden la capacidad diaria (${selectedMachine.daily_capacity_hours}h).`,
      );
    }
  }

  return warnings;
};

export default function MachineryPage() {
  const { company, can } = useAuth();
  const slug = company?.slug || "";
  const companyId = company?.id || "";

  const canRead = can("inventory.read");
  const canManage = can("inventory.manage");
  const canConfigure = can("inventory.configure");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const debouncedSearch = useDebounce(search, 250);

  const [selectedMachineryId, setSelectedMachineryId] = useState<string | null>(null);

  const {
    machinery,
    isLoadingMachinery,
    selectedMachineryShiftConfig,
    isLoadingSelectedShifts,
    createMachinery,
    updateMachinery,
    deleteMachinery,
    updateDailyCapacity,
    updateTechnicalCapacity,
    createShift,
    updateShift,
    deleteShift,
  } = useMachinery(slug, {
    search: debouncedSearch,
    status: statusFilter === "all" ? undefined : statusFilter,
    selectedMachineryId,
  });

  const selectedMachinery = useMemo(
    () => machinery.find((machine) => machine.id === selectedMachineryId) || null,
    [machinery, selectedMachineryId],
  );

  const [isMachineDialogOpen, setIsMachineDialogOpen] = useState(false);
  const [isDailyCapacityDialogOpen, setIsDailyCapacityDialogOpen] = useState(false);
  const [isTechnicalCapacityDialogOpen, setIsTechnicalCapacityDialogOpen] = useState(false);
  const [isShiftsDialogOpen, setIsShiftsDialogOpen] = useState(false);

  const [editingMachine, setEditingMachine] = useState<Machinery | null>(null);
  const [editingShift, setEditingShift] = useState<MachineryShift | null>(null);

  const [machineForm, setMachineForm] = useState(emptyMachineForm);
  const [dailyCapacityValue, setDailyCapacityValue] = useState("8");
  const [technicalCapacityForm, setTechnicalCapacityForm] = useState({
    technical_capacity_ratio: "0",
    technical_capacity_unit: "",
    technical_capacity_interval_minutes: "60",
  });
  const [shiftForm, setShiftForm] = useState(emptyShiftForm);

  useEffect(() => {
    if (!isMachineDialogOpen) {
      setEditingMachine(null);
      setMachineForm(emptyMachineForm);
    }
  }, [isMachineDialogOpen]);

  useEffect(() => {
    if (!isShiftsDialogOpen) {
      setEditingShift(null);
      setShiftForm(emptyShiftForm);
      setSelectedMachineryId(null);
    }
  }, [isShiftsDialogOpen]);

  const shiftWarnings = useMemo(() => {
    return calculateShiftFormWarnings(
      shiftForm,
      selectedMachineryShiftConfig?.shifts || [],
      selectedMachinery,
      editingShift?.id || null,
    );
  }, [shiftForm, selectedMachineryShiftConfig?.shifts, selectedMachinery, editingShift]);

  const openCreateMachine = () => {
    setEditingMachine(null);
    setMachineForm({
      ...emptyMachineForm,
      acquisition_date: new Date().toISOString().split("T")[0],
    });
    setIsMachineDialogOpen(true);
  };

  const openEditMachine = (machine: Machinery) => {
    setEditingMachine(machine);
    setMachineForm({
      name: machine.name,
      type: machine.type,
      description: machine.description || "",
      acquisition_date: machine.acquisition_date,
      status: machine.status,
      daily_capacity_hours: machine.daily_capacity_hours.toString(),
      technical_capacity_ratio: machine.technical_capacity_ratio.toString(),
      technical_capacity_unit: machine.technical_capacity_unit || "",
      technical_capacity_interval_minutes: machine.technical_capacity_interval_minutes.toString(),
    });
    setIsMachineDialogOpen(true);
  };

  const openDailyCapacityDialog = (machine: Machinery) => {
    setSelectedMachineryId(machine.id);
    setDailyCapacityValue(machine.daily_capacity_hours.toString());
    setIsDailyCapacityDialogOpen(true);
  };

  const openTechnicalCapacityDialog = (machine: Machinery) => {
    setSelectedMachineryId(machine.id);
    setTechnicalCapacityForm({
      technical_capacity_ratio: machine.technical_capacity_ratio.toString(),
      technical_capacity_unit: machine.technical_capacity_unit || "",
      technical_capacity_interval_minutes: machine.technical_capacity_interval_minutes.toString(),
    });
    setIsTechnicalCapacityDialogOpen(true);
  };

  const openShiftsDialog = (machine: Machinery) => {
    setSelectedMachineryId(machine.id);
    setEditingShift(null);
    setShiftForm(emptyShiftForm);
    setIsShiftsDialogOpen(true);
  };

  const handleSaveMachine = async () => {
    if (!machineForm.name.trim() || !machineForm.type.trim() || !machineForm.acquisition_date) {
      toast.error("Nombre, tipo y fecha de adquisición son requeridos");
      return;
    }

    const ratioValue = Number(machineForm.technical_capacity_ratio || 0);
    if (ratioValue > 0 && !machineForm.technical_capacity_unit.trim()) {
      toast.error("Define la unidad técnica cuando el ratio es mayor a 0");
      return;
    }

    const payload = {
      slug,
      company_id: companyId,
      name: machineForm.name.trim(),
      type: machineForm.type.trim(),
      description: machineForm.description.trim() || undefined,
      acquisition_date: machineForm.acquisition_date,
      status: machineForm.status,
      daily_capacity_hours: Number(machineForm.daily_capacity_hours || 0),
      technical_capacity_ratio: ratioValue,
      technical_capacity_unit: machineForm.technical_capacity_unit.trim() || undefined,
      technical_capacity_interval_minutes: Number(machineForm.technical_capacity_interval_minutes || 60),
    };

    try {
      if (editingMachine) {
        await updateMachinery.mutateAsync({ id: editingMachine.id, payload });
      } else {
        await createMachinery.mutateAsync(payload);
      }
      setIsMachineDialogOpen(false);
    } catch {
      // Error handled by hook
    }
  };

  const handleDeleteMachine = async (machine: Machinery) => {
    if (!canManage) return;
    if (!window.confirm(`¿Desactivar la máquina "${machine.name}"?`)) return;

    try {
      await deleteMachinery.mutateAsync({ id: machine.id, company_id: companyId });
    } catch {
      // Error handled by hook
    }
  };

  const handleSaveDailyCapacity = async () => {
    if (!selectedMachineryId) return;

    const parsedValue = Number(dailyCapacityValue);
    if (!Number.isFinite(parsedValue) || parsedValue < 0 || parsedValue > 24) {
      toast.error("La capacidad diaria debe estar entre 0 y 24 horas");
      return;
    }

    try {
      await updateDailyCapacity.mutateAsync({
        id: selectedMachineryId,
        payload: {
          slug,
          company_id: companyId,
          daily_capacity_hours: parsedValue,
        },
      });
      setIsDailyCapacityDialogOpen(false);
    } catch {
      // Error handled by hook
    }
  };

  const handleSaveTechnicalCapacity = async () => {
    if (!selectedMachineryId) return;

    const ratioValue = Number(technicalCapacityForm.technical_capacity_ratio || 0);
    const intervalValue = Number(technicalCapacityForm.technical_capacity_interval_minutes || 60);

    if (!Number.isFinite(ratioValue) || ratioValue < 0) {
      toast.error("El ratio técnico debe ser mayor o igual a 0");
      return;
    }

    if (ratioValue > 0 && !technicalCapacityForm.technical_capacity_unit.trim()) {
      toast.error("Define la unidad técnica cuando el ratio es mayor a 0");
      return;
    }

    if (!Number.isInteger(intervalValue) || intervalValue <= 0) {
      toast.error("El intervalo técnico debe ser un entero mayor a 0");
      return;
    }

    try {
      await updateTechnicalCapacity.mutateAsync({
        id: selectedMachineryId,
        payload: {
          slug,
          company_id: companyId,
          technical_capacity_ratio: ratioValue,
          technical_capacity_unit: technicalCapacityForm.technical_capacity_unit.trim() || undefined,
          technical_capacity_interval_minutes: intervalValue,
        },
      });
      setIsTechnicalCapacityDialogOpen(false);
    } catch {
      // Error handled by hook
    }
  };

  const handleSaveShift = async () => {
    if (!selectedMachineryId) return;

    if (shiftWarnings.length > 0) {
      toast.error("Corrige los conflictos del turno antes de guardar");
      return;
    }

    const payload = {
      slug,
      company_id: companyId,
      day_of_week: Number(shiftForm.day_of_week),
      start_time: shiftForm.start_time,
      end_time: shiftForm.end_time,
      is_active: shiftForm.is_active,
    };

    try {
      if (editingShift) {
        await updateShift.mutateAsync({
          id: selectedMachineryId,
          shiftId: editingShift.id,
          payload,
        });
      } else {
        await createShift.mutateAsync({ id: selectedMachineryId, payload });
      }

      setEditingShift(null);
      setShiftForm(emptyShiftForm);
    } catch {
      // Error handled by hook
    }
  };

  const handleEditShift = (shift: MachineryShift) => {
    setEditingShift(shift);
    setShiftForm({
      day_of_week: String(shift.day_of_week),
      start_time: shift.display_start_time,
      end_time: shift.display_end_time,
      is_active: shift.is_active,
    });
  };

  const handleDeleteShift = async (shift: MachineryShift) => {
    if (!selectedMachineryId) return;
    if (!window.confirm("¿Desactivar este turno?")) return;

    try {
      await deleteShift.mutateAsync({
        id: selectedMachineryId,
        shiftId: shift.id,
        company_id: companyId,
      });
    } catch {
      // Error handled by hook
    }
  };

  const shiftConflicts = selectedMachineryShiftConfig?.conflicts || [];

  if (!canRead) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle>Sin acceso</CardTitle>
            <CardDescription>No tienes permisos para visualizar el módulo de maquinaria.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Gestión de Maquinaria
          </h1>
          <p className="text-sm text-slate-500">
            Control centralizado de estado, capacidad operativa y turnos por equipo.
          </p>
        </div>

        {canManage && (
          <Button className="gap-2 bg-black hover:bg-gray-800 text-white" onClick={openCreateMachine}>
            <Plus className="h-4 w-4" />
            Nueva máquina
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="grid gap-3 md:grid-cols-[1fr_220px]">
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar por nombre, tipo o descripción"
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filtrar por estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                {MACHINERY_STATUSES.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {isLoadingMachinery ? (
        <CardGridSkeleton hasMetrics={false} />
      ) : machinery.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Sin maquinaria registrada</CardTitle>
            <CardDescription>
              Crea el primer equipo para empezar a configurar su capacidad y turnos.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {machinery.map((machine) => (
            <Card key={machine.id} className="border-slate-200">
              <CardHeader className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-lg">{machine.name}</CardTitle>
                    <CardDescription>{machine.type}</CardDescription>
                  </div>
                  <Badge className={statusColorMap[machine.status]}>{machine.status}</Badge>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                <div className="space-y-1 text-sm text-slate-600">
                  <p>
                    <span className="font-medium">Adquisición:</span> {formatDate(machine.acquisition_date)}
                  </p>
                  <p>
                    <span className="font-medium">Capacidad diaria:</span> {machine.daily_capacity_hours} h/día
                  </p>
                  <p>
                    <span className="font-medium">Capacidad técnica:</span>{" "}
                    {machine.technical_capacity_display || "No configurada"}
                  </p>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-700">Turnos activos ({machine.active_shifts_count})</p>
                  {machine.shift_summary.length > 0 ? (
                    <div className="space-y-1">
                      {machine.shift_summary.slice(0, 2).map((summaryLine) => (
                        <p key={summaryLine} className="text-xs text-slate-600 leading-relaxed">
                          {summaryLine}
                        </p>
                      ))}
                      {machine.shift_summary.length > 2 && (
                        <p className="text-xs text-slate-500">
                          +{machine.shift_summary.length - 2} días con turnos
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500">Sin turnos activos.</p>
                  )}
                </div>

                {machine.has_capacity_conflicts && (
                  <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                    <AlertTriangle className="mt-0.5 h-4 w-4" />
                    Existen conflictos entre turnos y capacidad diaria.
                  </div>
                )}

                {machine.description && (
                  <p className="text-xs text-slate-500 border-t pt-3">{machine.description}</p>
                )}

                <div className="grid grid-cols-2 gap-2 pt-2">
                  {canManage && (
                    <Button variant="outline" size="sm" className="gap-2" onClick={() => openEditMachine(machine)}>
                      <Pencil className="h-3.5 w-3.5" /> Editar
                    </Button>
                  )}

                  {canConfigure && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => openDailyCapacityDialog(machine)}
                    >
                      <Clock3 className="h-3.5 w-3.5" /> Cap. diaria
                    </Button>
                  )}

                  {canConfigure && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => openTechnicalCapacityDialog(machine)}
                    >
                      <Gauge className="h-3.5 w-3.5" /> Cap. técnica
                    </Button>
                  )}

                  {canConfigure && (
                    <Button variant="outline" size="sm" className="gap-2" onClick={() => openShiftsDialog(machine)}>
                      <Settings2 className="h-3.5 w-3.5" /> Turnos
                    </Button>
                  )}
                </div>

                {canManage && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDeleteMachine(machine)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Desactivar máquina
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={isMachineDialogOpen} onOpenChange={setIsMachineDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingMachine ? "Editar maquinaria" : "Registrar maquinaria"}</DialogTitle>
            <DialogDescription>
              Define datos generales, estado y capacidades operativas del equipo.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input
                value={machineForm.name}
                onChange={(event) => setMachineForm((prev) => ({ ...prev, name: event.target.value }))}
                placeholder="Ej: Prensa Hidráulica #2"
              />
            </div>

            <div className="space-y-2">
              <Label>Tipo</Label>
              <Input
                value={machineForm.type}
                onChange={(event) => setMachineForm((prev) => ({ ...prev, type: event.target.value }))}
                placeholder="Ej: Corte, Impresión, Mezclado"
              />
            </div>

            <div className="space-y-2">
              <Label>Fecha de adquisición</Label>
              <Input
                type="date"
                value={machineForm.acquisition_date}
                onChange={(event) =>
                  setMachineForm((prev) => ({ ...prev, acquisition_date: event.target.value }))
                }
              />
            </div>

            <div className="space-y-2">
              <Label>Estado</Label>
              <Select
                value={machineForm.status}
                onValueChange={(value: MachineryStatus) =>
                  setMachineForm((prev) => ({ ...prev, status: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MACHINERY_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Capacidad diaria (horas)</Label>
              <Input
                type="number"
                min={0}
                max={24}
                step="0.25"
                value={machineForm.daily_capacity_hours}
                onChange={(event) =>
                  setMachineForm((prev) => ({ ...prev, daily_capacity_hours: event.target.value }))
                }
              />
            </div>

            <div className="space-y-2">
              <Label>Ratio técnico</Label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={machineForm.technical_capacity_ratio}
                onChange={(event) =>
                  setMachineForm((prev) => ({ ...prev, technical_capacity_ratio: event.target.value }))
                }
                placeholder="Ej: 120"
              />
            </div>

            <div className="space-y-2">
              <Label>Unidad técnica</Label>
              <Input
                value={machineForm.technical_capacity_unit}
                onChange={(event) =>
                  setMachineForm((prev) => ({ ...prev, technical_capacity_unit: event.target.value }))
                }
                placeholder="Ej: unidades"
              />
            </div>

            <div className="space-y-2">
              <Label>Intervalo técnico (min)</Label>
              <Input
                type="number"
                min={1}
                step="1"
                value={machineForm.technical_capacity_interval_minutes}
                onChange={(event) =>
                  setMachineForm((prev) => ({
                    ...prev,
                    technical_capacity_interval_minutes: event.target.value,
                  }))
                }
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label>Descripción</Label>
              <Textarea
                value={machineForm.description}
                onChange={(event) =>
                  setMachineForm((prev) => ({ ...prev, description: event.target.value }))
                }
                placeholder="Detalles operativos o notas relevantes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMachineDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveMachine}>{editingMachine ? "Guardar cambios" : "Registrar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isDailyCapacityDialogOpen} onOpenChange={setIsDailyCapacityDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Configurar capacidad diaria</DialogTitle>
            <DialogDescription>
              {selectedMachinery ? `Máquina: ${selectedMachinery.name}` : ""}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2">
            <Label>Horas disponibles por día</Label>
            <Input
              type="number"
              min={0}
              max={24}
              step="0.25"
              value={dailyCapacityValue}
              onChange={(event) => setDailyCapacityValue(event.target.value)}
            />
            <p className="text-xs text-slate-500">
              La suma de turnos activos por día no puede exceder esta capacidad.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDailyCapacityDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveDailyCapacity}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isTechnicalCapacityDialogOpen} onOpenChange={setIsTechnicalCapacityDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Configurar capacidad técnica</DialogTitle>
            <DialogDescription>
              {selectedMachinery ? `Máquina: ${selectedMachinery.name}` : ""}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label>Ratio</Label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={technicalCapacityForm.technical_capacity_ratio}
                onChange={(event) =>
                  setTechnicalCapacityForm((prev) => ({
                    ...prev,
                    technical_capacity_ratio: event.target.value,
                  }))
                }
              />
            </div>

            <div className="space-y-2">
              <Label>Unidad (ej: unidades, kg, litros)</Label>
              <Input
                value={technicalCapacityForm.technical_capacity_unit}
                onChange={(event) =>
                  setTechnicalCapacityForm((prev) => ({
                    ...prev,
                    technical_capacity_unit: event.target.value,
                  }))
                }
              />
            </div>

            <div className="space-y-2">
              <Label>Intervalo (minutos)</Label>
              <Input
                type="number"
                min={1}
                step={1}
                value={technicalCapacityForm.technical_capacity_interval_minutes}
                onChange={(event) =>
                  setTechnicalCapacityForm((prev) => ({
                    ...prev,
                    technical_capacity_interval_minutes: event.target.value,
                  }))
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsTechnicalCapacityDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveTechnicalCapacity}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isShiftsDialogOpen} onOpenChange={setIsShiftsDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5 text-slate-600" />
              Turnos de operación
            </DialogTitle>
            <DialogDescription>
              {selectedMachinery
                ? `${selectedMachinery.name} · Capacidad diaria: ${selectedMachinery.daily_capacity_hours} h`
                : ""}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2 lg:grid-cols-[340px_1fr]">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {editingShift ? "Editar turno" : "Nuevo turno"}
                </CardTitle>
                <CardDescription>
                  Puedes definir múltiples rangos por día mientras no se solapen.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-3">
                <div className="space-y-2">
                  <Label>Día</Label>
                  <Select
                    value={shiftForm.day_of_week}
                    onValueChange={(value) => setShiftForm((prev) => ({ ...prev, day_of_week: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MACHINERY_DAYS.map((day) => (
                        <SelectItem key={day.id} value={String(day.id)}>
                          {day.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label>Inicio</Label>
                    <Input
                      type="time"
                      value={shiftForm.start_time}
                      onChange={(event) =>
                        setShiftForm((prev) => ({ ...prev, start_time: event.target.value }))
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Fin</Label>
                    <Input
                      type="time"
                      value={shiftForm.end_time}
                      onChange={(event) =>
                        setShiftForm((prev) => ({ ...prev, end_time: event.target.value }))
                      }
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between rounded-md border px-3 py-2">
                  <Label className="text-sm">Turno activo</Label>
                  <Switch
                    checked={shiftForm.is_active}
                    onCheckedChange={(checked) => setShiftForm((prev) => ({ ...prev, is_active: checked }))}
                  />
                </div>

                {shiftWarnings.length > 0 && (
                  <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700 space-y-1">
                    {shiftWarnings.map((warning) => (
                      <p key={warning}>{warning}</p>
                    ))}
                  </div>
                )}

                <div className="flex gap-2">
                  <Button className="flex-1" onClick={handleSaveShift}>
                    {editingShift ? "Actualizar" : "Agregar"}
                  </Button>
                  {editingShift && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditingShift(null);
                        setShiftForm(emptyShiftForm);
                      }}
                    >
                      Cancelar
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Turnos configurados</CardTitle>
                <CardDescription>
                  Lista de rangos por día y estado del turno.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                {isLoadingSelectedShifts ? (
                  <p className="text-sm text-slate-500">Cargando turnos...</p>
                ) : (selectedMachineryShiftConfig?.shifts?.length || 0) === 0 ? (
                  <p className="text-sm text-slate-500">Sin turnos registrados para esta máquina.</p>
                ) : (
                  <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1">
                    {selectedMachineryShiftConfig?.shifts.map((shift) => (
                      <div
                        key={shift.id}
                        className="rounded-md border border-slate-200 px-3 py-2 flex items-center justify-between gap-2"
                      >
                        <div>
                          <p className="text-sm font-medium">
                            {shift.day_label} · {shift.display_start_time}-{shift.display_end_time}
                          </p>
                          <p className="text-xs text-slate-500">Duración: {shift.duration_hours} h</p>
                        </div>

                        <div className="flex items-center gap-2">
                          <Badge
                            className={
                              shift.is_active
                                ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                                : "bg-slate-100 text-slate-600 border-slate-200"
                            }
                          >
                            {shift.is_active ? "Activo" : "Inactivo"}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditShift(shift)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteShift(shift)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {shiftConflicts.length > 0 && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 space-y-1">
                    <p className="font-medium flex items-center gap-1">
                      <AlertTriangle className="h-4 w-4" />
                      Conflictos detectados
                    </p>
                    {shiftConflicts.map((conflict, index) => (
                      <p key={`${conflict.type}-${index}`}>{conflict.message}</p>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsShiftsDialogOpen(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
